import axios from "axios";
import {
  Employee,
  LeaveType,
  LeaveBalance,
  LeaveRequest,
  CreateLeaveRequest,
  LeaveCalendarEvent,
  EmployeeDashboard,
  ApproverDashboard,
  AdminDashboard,
  BulkOperationResult,
  ValidationResult,
  ApiResponse,
} from "../types/leave";

const API_BASE_URL = "/ofc/leave";

// Configure axios defaults
axios.defaults.withCredentials = true;

class LeaveApiService {
  // Employee APIs
  async getMyProfile(): Promise<Employee> {
    const response = await axios.get<ApiResponse<Employee>>(
      `${API_BASE_URL}/employees/me`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to fetch profile");
    }
    return response.data.data;
  }

  async getMyBalances(): Promise<LeaveBalance[]> {
    const response = await axios.get<ApiResponse<LeaveBalance[]>>(
      `${API_BASE_URL}/employees/me/balances`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to fetch balances");
    }
    return response.data.data;
  }

  async getMyRequests(): Promise<LeaveRequest[]> {
    const response = await axios.get<ApiResponse<any>>(
      `${API_BASE_URL}/employees/me/requests`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to fetch requests");
    }
    // Handle paginated response - extract content array
    const data = response.data.data;
    if (data.content && Array.isArray(data.content)) {
      return data.content;
    }
    // Fallback for non-paginated response
    return Array.isArray(data) ? data : [];
  }

  async createLeaveRequest(request: CreateLeaveRequest): Promise<LeaveRequest> {
    // Add user's timezone for proper date conversion
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const requestWithTimezone = { ...request, timezone };

    const response = await axios.post<ApiResponse<LeaveRequest>>(
      `${API_BASE_URL}/employees/me/requests`,
      requestWithTimezone,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to create request");
    }
    return response.data.data;
  }

  async updateLeaveRequest(
    requestId: number,
    request: CreateLeaveRequest,
  ): Promise<LeaveRequest> {
    // Add user's timezone for proper date conversion
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const requestWithTimezone = { ...request, timezone };

    const response = await axios.put<ApiResponse<LeaveRequest>>(
      `${API_BASE_URL}/employees/me/requests/${requestId}`,
      requestWithTimezone,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to update request");
    }
    return response.data.data;
  }

  // Keep the old method name for backward compatibility
  async submitLeaveRequest(request: CreateLeaveRequest): Promise<LeaveRequest> {
    return this.createLeaveRequest(request);
  }

  async cancelLeaveRequest(requestId: number): Promise<LeaveRequest> {
    const response = await axios.put<ApiResponse<LeaveRequest>>(
      `${API_BASE_URL}/employees/me/requests/${requestId}/cancel`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to cancel request");
    }
    return response.data.data;
  }

  async getMyDashboard(): Promise<EmployeeDashboard> {
    const response = await axios.get<ApiResponse<EmployeeDashboard>>(
      `${API_BASE_URL}/employees/me/dashboard`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to fetch dashboard");
    }
    return response.data.data;
  }

  // Leave Request Management APIs (Approver)
  async getPendingRequests(): Promise<LeaveRequest[]> {
    const response = await axios.get<ApiResponse<LeaveRequest[]>>(
      `${API_BASE_URL}/requests/pending`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(
        response.data.error || "Failed to fetch pending requests",
      );
    }
    return response.data.data;
  }

  async getMyPendingRequests(): Promise<LeaveRequest[]> {
    const response = await axios.get<ApiResponse<LeaveRequest[]>>(
      `${API_BASE_URL}/requests/pending/me`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(
        response.data.error || "Failed to fetch my pending requests",
      );
    }
    return response.data.data;
  }

  async getPendingRequestsCount(): Promise<number> {
    const response = await axios.get<ApiResponse<number>>(
      `${API_BASE_URL}/requests/count/pending`,
    );
    if (!response.data.success || response.data.data === undefined) {
      throw new Error(
        response.data.error || "Failed to fetch pending requests count",
      );
    }
    return response.data.data;
  }

  async approveLeaveRequest(
    requestId: number,
    isPlanned?: boolean,
  ): Promise<LeaveRequest> {
    const params = new URLSearchParams();
    if (isPlanned !== undefined && isPlanned !== null) {
      params.append("isPlanned", String(isPlanned));
    }
    const queryString = params.toString();
    const url = `${API_BASE_URL}/requests/${requestId}/approve${queryString ? `?${queryString}` : ""}`;

    const response = await axios.put<ApiResponse<LeaveRequest>>(url);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to approve request");
    }
    return response.data.data;
  }

  async denyLeaveRequest(
    requestId: number,
    reason: string,
    isPlanned?: boolean,
  ): Promise<LeaveRequest> {
    const params = new URLSearchParams();
    params.append("reason", reason);
    if (isPlanned !== undefined && isPlanned !== null) {
      params.append("isPlanned", String(isPlanned));
    }

    const response = await axios.put<ApiResponse<LeaveRequest>>(
      `${API_BASE_URL}/requests/${requestId}/deny?${params.toString()}`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to deny request");
    }
    return response.data.data;
  }

  async getLeaveCalendar(
    startDate?: string,
    endDate?: string,
  ): Promise<LeaveCalendarEvent[]> {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);

    // Add user's timezone for proper date conversion
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    params.append("timezone", timezone);

    const response = await axios.get<ApiResponse<LeaveCalendarEvent[]>>(
      `${API_BASE_URL}/requests/calendar?${params}`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to fetch calendar");
    }
    return response.data.data;
  }

  async getTeamRequests(managerId: number): Promise<LeaveRequest[]> {
    const response = await axios.get<ApiResponse<LeaveRequest[]>>(
      `${API_BASE_URL}/requests/team/${managerId}`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to fetch team requests");
    }
    return response.data.data;
  }

  async getApproverDashboard(): Promise<ApproverDashboard> {
    const response = await axios.get<ApiResponse<ApproverDashboard>>(
      `${API_BASE_URL}/requests/dashboard/approver`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(
        response.data.error || "Failed to fetch approver dashboard",
      );
    }
    return response.data.data;
  }

  // Leave Types APIs
  async getLeaveTypes(): Promise<LeaveType[]> {
    const response = await axios.get<ApiResponse<LeaveType[]>>(
      `${API_BASE_URL}/leave-types`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to fetch leave types");
    }
    return response.data.data;
  }

  async getActiveLeaveTypes(): Promise<LeaveType[]> {
    const response = await axios.get<ApiResponse<LeaveType[]>>(
      `${API_BASE_URL}/leave-types/active`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(
        response.data.error || "Failed to fetch active leave types",
      );
    }
    return response.data.data;
  }

  // Admin APIs (Leave-specific)

  async getAdminDashboard(): Promise<AdminDashboard> {
    const response = await axios.get<ApiResponse<AdminDashboard>>(
      `${API_BASE_URL}/admin/statistics/dashboard`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to fetch admin dashboard");
    }
    return response.data.data;
  }

  // Balance Management APIs (Admin)
  async processAnnualAllocation(year: number): Promise<BulkOperationResult> {
    const response = await axios.post<ApiResponse<BulkOperationResult>>(
      `${API_BASE_URL}/balances/process-annual-allocation/${year}`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(
        response.data.error || "Failed to process annual allocation",
      );
    }
    return response.data.data;
  }

  async processAccrualIncrease(year: number): Promise<BulkOperationResult> {
    const response = await axios.post<ApiResponse<BulkOperationResult>>(
      `${API_BASE_URL}/balances/process-accrual-increase/${year}`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(
        response.data.error || "Failed to process accrual increase",
      );
    }
    return response.data.data;
  }

  async processCarryOver(
    fromYear: number,
    toYear: number,
  ): Promise<BulkOperationResult> {
    const response = await axios.post<ApiResponse<BulkOperationResult>>(
      `${API_BASE_URL}/balances/process-carry-over/${fromYear}/${toYear}`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to process carry over");
    }
    return response.data.data;
  }

  // Validation APIs
  async validateLeaveRequest(
    request: CreateLeaveRequest,
  ): Promise<ValidationResult> {
    const response = await axios.post<ApiResponse<ValidationResult>>(
      `${API_BASE_URL}/requests/validate`,
      request,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to validate request");
    }
    return response.data.data;
  }

  // Admin Leave Grant APIs
  async grantLeaveBalance(grantData: {
    employeeId: number;
    leaveTypeCode: string;
    year: number;
    daysToGrant: number;
    reason?: string;
    addToExisting?: boolean;
  }): Promise<string> {
    const response = await axios.post<ApiResponse<string>>(
      `${API_BASE_URL}/admin/grant-leave`,
      grantData,
    );
    if (!response.data.success) {
      throw new Error(response.data.error || "Failed to grant leave balance");
    }
    // Backend returns success=true with data=null, so we just return the message
    return response.data.message || "Leave balance granted successfully";
  }

  // Adjust leave balance (add or subtract)
  async adjustLeaveBalance(adjustData: {
    employeeId: number;
    leaveTypeId: number;
    year: number;
    adjustmentAmount: number;
    adjustmentType: "ADD_ALLOCATED" | "SUBTRACT_ALLOCATED";
    reason: string;
  }): Promise<LeaveBalance> {
    const response = await axios.put<ApiResponse<LeaveBalance>>(
      `${API_BASE_URL}/balances/adjust`,
      adjustData,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to adjust leave balance");
    }
    return response.data.data;
  }

  async getLeaveGrantHistory(
    employeeId: number,
    year: number,
  ): Promise<LeaveBalance[]> {
    const response = await axios.get<ApiResponse<LeaveBalance[]>>(
      `${API_BASE_URL}/admin/employees/${employeeId}/leave-grants?year=${year}`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(
        response.data.error || "Failed to fetch leave grant history",
      );
    }
    return response.data.data;
  }

  async getEmployeeBalancesAdmin(
    employeeId: number,
    year: number,
  ): Promise<LeaveBalance[]> {
    const response = await axios.get<ApiResponse<LeaveBalance[]>>(
      `${API_BASE_URL}/admin/employees/${employeeId}/balances?year=${year}`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(
        response.data.error || "Failed to fetch employee balances",
      );
    }
    return response.data.data;
  }

  // Get all employees for admin leave credit management
  async getAllEmployees(
    page: number = 0,
    size: number = 20,
  ): Promise<Employee[]> {
    const response = await axios.get<ApiResponse<{ content: Employee[] }>>(
      `${API_BASE_URL}/employees?page=${page}&size=${size}`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to fetch employees");
    }
    return response.data.data.content || [];
  }

  // Search employees for admin leave credit management
  async searchEmployees(
    searchTerm: string,
    page: number = 0,
    size: number = 20,
  ): Promise<Employee[]> {
    const response = await axios.get<ApiResponse<{ content: Employee[] }>>(
      `${API_BASE_URL}/employees/search?searchTerm=${encodeURIComponent(searchTerm)}&page=${page}&size=${size}`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to search employees");
    }
    return response.data.data.content || [];
  }

  // Get employee leave requests by year (Admin)
  async getEmployeeLeaveRequestsByYear(
    employeeId: number,
    year: number,
  ): Promise<LeaveRequest[]> {
    const response = await axios.get<ApiResponse<LeaveRequest[]>>(
      `${API_BASE_URL}/requests/employee/${employeeId}/year/${year}`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(
        response.data.error || "Failed to fetch employee leave requests",
      );
    }
    return response.data.data;
  }

  // Delete leave request (Admin only)
  async deleteLeaveRequest(requestId: number): Promise<string> {
    const response = await axios.delete<ApiResponse<string>>(
      `${API_BASE_URL}/admin/requests/${requestId}`,
    );
    if (!response.data.success) {
      throw new Error(response.data.error || "Failed to delete leave request");
    }
    return response.data.data || "Leave request deleted successfully";
  }

  async getAllLeaveRequestsWithFilters(filters: {
    startDate?: string;
    endDate?: string;
    employeeName?: string;
    leaveTypeId?: number;
    status?: string;
  }): Promise<LeaveRequest[]> {
    const params = new URLSearchParams();
    if (filters.startDate) params.append("startDate", filters.startDate);
    if (filters.endDate) params.append("endDate", filters.endDate);
    if (filters.employeeName)
      params.append("employeeName", filters.employeeName);
    if (filters.leaveTypeId)
      params.append("leaveTypeId", String(filters.leaveTypeId));
    if (filters.status) params.append("status", filters.status);

    const queryString = params.toString();
    const url = `${API_BASE_URL}/requests/list${queryString ? `?${queryString}` : ""}`;

    const response = await axios.get<ApiResponse<LeaveRequest[]>>(url);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to fetch leave requests");
    }
    return response.data.data;
  }

  async updatePlannedStatus(
    requestId: number,
    isPlanned: boolean,
  ): Promise<LeaveRequest> {
    const response = await axios.patch<ApiResponse<LeaveRequest>>(
      `${API_BASE_URL}/requests/${requestId}/planned-status`,
      null,
      { params: { isPlanned } },
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to update planned status");
    }
    return response.data.data;
  }
}

export default new LeaveApiService();
